/** @jsxImportSource @emotion/react */
import { css } from '@emotion/react'

import React from 'react'
import { Dialog, Drawer, SwipeableDrawer } from '@mui/material'
import { View } from '@components/atoms/views/View'
import { Pressable } from '@components/atoms/button/Pressable'
import { Icon } from '@components/atoms/icons/Icon'
import { mono, boFont, boMobile, boZ, boShared } from './backoffice.tokens'
import { useBoSizeClass } from './useBoSizeClass'

interface PropsType {
	open: boolean
	onClose: () => void
	title?: string
	children?: React.ReactNode
	/** 하단 고정 줄 — 「적용」·「저장」 같은 버튼 */
	footer?: React.ReactNode
	/** 폰 바텀 시트 최대 높이(dvh). 기본 85 */
	maxHeightVh?: number
	className?: string
}

/**
 * 시트 한 종류가 폭 등급으로 형태를 바꾼다 (RESPONSIVE_PLAN §7).
 * 폰 = 바텀 시트(드래그 핸들, 아래로 끌어 닫기) · 태블릿 = 우측 사이드 시트 440 · 데스크톱 = Dialog.
 * 안의 내용은 같다 — 필터 바·정렬·편집 폼이 그대로 들어온다.
 */
export const BoSheet = (props: PropsType) => {
	const size = useBoSizeClass()
	const maxHeight = `${props.maxHeightVh ?? 85}dvh`

	const head = (
		<View className="boSheetHead">
			{size === 'phone' ? <View className="boSheetHandle" /> : null}
			<View className="boSheetTitleRow">
				<span className="boSheetTitle">{props.title ?? ''}</span>
				<Pressable className="boSheetClose" onClick={props.onClose} aria-label="닫기">
					<Icon name="close" className="boSheetCloseIcon" />
				</Pressable>
			</View>
		</View>
	)

	const body = (
		<View className={`boSheetBody ${props.className ?? ''}`}>{props.children}</View>
	)

	const foot = props.footer ? <View className="boSheetFoot">{props.footer}</View> : null

	if (size === 'phone') {
		return (
			<SwipeableDrawer
				anchor="bottom"
				open={props.open}
				onClose={props.onClose}
				onOpen={() => {}}
				disableSwipeToOpen
				ModalProps={{ keepMounted: false }}
				PaperProps={{ className: 'boSheetPaper phone' }}
				css={style}
				sx={{ zIndex: boZ.sheet }}
			>
				<View className="boSheetInner" css={{ maxHeight }}>
					{head}
					{body}
					{foot}
				</View>
			</SwipeableDrawer>
		)
	}

	if (size === 'tablet') {
		return (
			<Drawer
				anchor="right"
				variant="temporary"
				open={props.open}
				onClose={props.onClose}
				PaperProps={{ className: 'boSheetPaper side' }}
				css={style}
				sx={{ zIndex: boZ.sheet }}
			>
				<View className="boSheetInner">
					{head}
					{body}
					{foot}
				</View>
			</Drawer>
		)
	}

	return (
		<Dialog
			open={props.open}
			onClose={props.onClose}
			PaperProps={{ className: 'boSheetPaper dialog' }}
			css={style}
			sx={{ zIndex: boZ.dialog }}
		>
			<View className="boSheetInner">
				{head}
				{body}
				{foot}
			</View>
		</Dialog>
	)
}

const style = css({
	'.boSheetPaper': {
		...boShared,
		fontFamily: boFont.ui,
		background: mono.surface,
		boxShadow: 'none',
		borderRadius: 0,
		color: mono.ink,

		'&.phone': {
			borderTop: `1px solid ${mono.line}`,
			paddingBottom: 'env(safe-area-inset-bottom, 0px)',
		},
		'&.side': {
			width: boMobile.sideSheetW,
			maxWidth: '92vw',
			borderLeft: `1px solid ${mono.line}`,
		},
		'&.dialog': {
			width: 520,
			maxWidth: '92vw',
			border: `1px solid ${mono.line}`,
		},
	},

	'.boSheetInner': {
		display: 'flex',
		flexDirection: 'column',
		minHeight: 0,
		height: '100%',
	},

	'.boSheetHead': {
		flexShrink: 0,
		padding: '0 16px',
		borderBottom: `1px solid ${mono.line}`,

		'.boSheetHandle': {
			width: 36,
			height: 4,
			margin: '10px auto 2px',
			borderRadius: 2,
			background: mono.lineSoft,
		},
		'.boSheetTitleRow': {
			display: 'flex',
			alignItems: 'center',
			justifyContent: 'space-between',
			height: 48,
		},
		'.boSheetTitle': {
			fontSize: 15,
			fontWeight: 500,
			color: mono.ink,
		},
		'.boSheetClose': {
			display: 'flex',
			alignItems: 'center',
			justifyContent: 'center',
			width: 44,
			height: 44,
			marginRight: -12,
			'.boSheetCloseIcon': { fontSize: 22 },
		},
	},

	'.boSheetBody': {
		flex: 1,
		minHeight: 0,
		overflowY: 'auto',
		overscrollBehaviorY: 'contain',
		padding: '16px',
		WebkitOverflowScrolling: 'touch',
	},

	'.boSheetFoot': {
		flexShrink: 0,
		display: 'flex',
		gap: 8,
		padding: '12px 16px',
		borderTop: `1px solid ${mono.line}`,
		background: mono.surface,
		'& > *': { flex: 1 },
	},
})
